// P12 — Toast notifications (système simple sans dépendance externe)
// Inspiré de shadcn/ui Toast mais version maison (pas besoin de radix-ui pour MVP).
import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { CheckCircle, AlertCircle, Info, AlertTriangle, X } from "lucide-react";
import { ht } from "@/lib/ht";

export type ToastVariant = "success" | "error" | "warning" | "info";

export interface Toast {
  id: number;
  title: string;
  description?: string;
  variant: ToastVariant;
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  toast: (t: Omit<Toast, "id">) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const VARIANT_STYLES: Record<ToastVariant, { bg: string; icon: ReactNode; border: string }> = {
  success: {
    bg: "bg-green-50 text-green-900",
    border: "border-green-500",
    icon: <CheckCircle className="h-5 w-5 text-green-600" />,
  },
  error: {
    bg: "bg-red-50 text-red-900",
    border: "border-red-500",
    icon: <AlertCircle className="h-5 w-5 text-red-600" />,
  },
  warning: {
    bg: "bg-amber-50 text-amber-900",
    border: "border-amber-500",
    icon: <AlertTriangle className="h-5 w-5 text-amber-600" />,
  },
  info: {
    bg: "bg-blue-50 text-blue-900",
    border: "border-blue-500",
    icon: <Info className="h-5 w-5 text-blue-600" />,
  },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (t: Omit<Toast, "id">) => {
      const id = Date.now() + Math.random();
      const newToast: Toast = { id, ...t };
      setToasts((prev) => [...prev, newToast]);
      const duration = t.duration ?? 4000;
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={{ toasts, toast, dismiss }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return ctx;
}

function ToastContainer({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((t) => {
        const style = VARIANT_STYLES[t.variant];
        return (
          <div
            key={t.id}
            className={`pointer-events-auto flex w-96 items-start gap-3 rounded-md border-l-4 ${style.border} ${style.bg} p-3 shadow-lg`}
            role="alert"
          >
            <div className="mt-0.5">{style.icon}</div>
            <div className="flex-1">
              <div className="text-sm font-semibold">{t.title}</div>
              {t.description && (
                <div className="mt-1 text-xs opacity-80">{t.description}</div>
              )}
            </div>
            <button
              type="button"
              onClick={() => onDismiss(t.id)}
              className="ml-2 opacity-60 hover:opacity-100"
              aria-label={ht("Fermer")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}